/**
 * Google Gemini `generateContent` / `streamGenerateContent` 适配器（T15a）。
 *
 * 与 `anthropic.ts` 一样，只做「协议翻译」，网络层完全复用 T03 的 `http.ts`：
 * 超时、重试、取消、SSE 半行拼接、错误规范化都不在这里重写。
 *
 * 关键行为：
 * - 请求体按 `contents[].parts[].text` + `systemInstruction` + `generationConfig`；
 * - 流式：`streamGenerateContent?alt=sse`，逐条 `data:` 负载映射为统一的 `StreamChunk`；
 * - 非流式：一次性产出思考与文本，再产出恰好一个 `done`；
 * - 取消：`signal.aborted` 后静默结束迭代，不产出任何分片（含 error），与 T03 契约一致；
 * - 流提前结束（内联 error / 取消）时，主动释放底层 response body。
 */

import {
  clampRetries,
  clampRetryDelayMs,
  clampTimeoutMs,
  isAbortError,
  jsonHeaders,
  openResponseWithRetry,
  ProviderHttpError,
  readJsonBody,
  readSSEData,
  resolveDeps,
  streamHeaders,
  toProviderError,
  type OpenResponseResult,
} from './http';
import { readModelCatalog } from './modelCatalog';
import type {
  ChatRequest,
  FetchLike,
  ProviderAdapter,
  ProviderDeps,
  ResolvedProviderConfig,
  StreamChunk,
  StreamUsage,
  ToolSpec,
} from './types';

export const GEMINI_KIND = 'gemini' as const;

/** 版本段：`/v1`、`/v1beta` 等。 */
const VERSION_SUFFIX = /\/v\d+[a-z0-9-]*$/i;

/**
 * 拼出 Gemini 端点。
 *
 * `version` 允许覆盖（Gemini 常用 `v1beta`）；baseUrl 已带版本段时以它为准。
 * `key` 以 query 参数下发（Gemini 的鉴权方式），因此必须走 `URL` 拼接。
 */
function geminiEndpoint(options: {
  rawBaseUrl: string;
  model: string;
  method: string;
  query?: Record<string, string>;
}): string {
  const { rawBaseUrl, model, method, query } = options;
  const trimmed = (rawBaseUrl ?? '').trim();
  if (!trimmed) {
    throw new ProviderHttpError('baseUrl 为空', 'config');
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new ProviderHttpError(`baseUrl 非法: ${rawBaseUrl}`, 'config');
  }

  const pathname = parsed.pathname.replace(/\/+$/, '');
  const base = VERSION_SUFFIX.test(pathname) ? pathname : `${pathname}/v1beta`;
  parsed.pathname = `${base}/models/${model}:${method}`;
  for (const [key, value] of Object.entries(query ?? {})) {
    parsed.searchParams.set(key, value);
  }
  return parsed.toString();
}

/** `POST /v1beta/models/{model}:generateContent`。 */
export function geminiGenerateUrl(rawBaseUrl: string, model: string, apiKey?: string): string {
  return geminiEndpoint({
    rawBaseUrl,
    model,
    method: 'generateContent',
    query: apiKey ? { key: apiKey } : undefined,
  });
}

/** `POST /v1beta/models/{model}:streamGenerateContent?alt=sse`。 */
export function geminiStreamUrl(rawBaseUrl: string, model: string, apiKey?: string): string {
  const query: Record<string, string> = { alt: 'sse' };
  if (apiKey) query.key = apiKey;
  return geminiEndpoint({ rawBaseUrl, model, method: 'streamGenerateContent', query });
}

/** `GET /v1beta/models`。 */
export function geminiModelsUrl(rawBaseUrl: string, apiKey?: string): string {
  const trimmed = (rawBaseUrl ?? '').trim();
  if (!trimmed) {
    throw new ProviderHttpError('baseUrl 为空', 'config');
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new ProviderHttpError(`baseUrl 非法: ${rawBaseUrl}`, 'config');
  }

  const pathname = parsed.pathname.replace(/\/+$/, '');
  parsed.pathname = VERSION_SUFFIX.test(pathname) ? `${pathname}/models` : `${pathname}/v1beta/models`;
  if (apiKey) parsed.searchParams.set('key', apiKey);
  return parsed.toString();
}

/** Gemini 用 `x-goog-api-key`，不用 OpenAI 的 `Authorization: Bearer`。 */
export function geminiHeaders(options: {
  stream: boolean;
  apiKey?: string;
  extra?: Record<string, string>;
}): Record<string, string> {
  const { stream, apiKey, extra } = options;
  const base = stream ? streamHeaders(undefined, extra) : jsonHeaders(undefined, extra);
  const headers: Record<string, string> = { ...base };
  if (apiKey) headers['x-goog-api-key'] = apiKey;
  return headers;
}

export interface GeminiPart {
  text?: string;
  thought?: boolean;
  functionCall?: { name?: string; args?: unknown };
  function_call?: { name?: string; args?: unknown };
  inlineData?: { mimeType: string; data: string };
  fileData?: { fileUri: string; mimeType?: string };
  [key: string]: unknown;
}

export interface GeminiContent {
  role: 'user' | 'model';
  parts: GeminiPart[];
}

export interface GeminiRequestBody {
  contents: GeminiContent[];
  systemInstruction?: { parts: { text: string }[] };
  generationConfig?: {
    temperature?: number;
    topP?: number;
    maxOutputTokens?: number;
    thinkingConfig?: { thinkingBudget: number };
  };
  tools?: Record<string, unknown>[];
  [key: string]: unknown;
}

/** 思考等级 -> Gemini 思考预算（token）。`off` 不下发 `thinkingConfig`。 */
const THINKING_BUDGET: Record<string, number | undefined> = {
  off: undefined,
  low: 1024,
  medium: 4096,
  high: 16384,
};

/** `maxOutputTokens` 必须为正整数。 */
function normalizeMaxTokens(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  return Math.floor(value);
}

/** 用户 system 注入优先；仅无用户注入时使用 settings 回退提示词。 */
function collectSystemPrompt(req: ChatRequest): string | undefined {
  const userParts: string[] = [];
  for (const message of req.messages) {
    if (message.role === 'system' && message.content.trim()) userParts.push(message.content);
  }
  if (userParts.length > 0) return userParts.join('\n\n');
  return req.settings.systemPrompt?.trim() ? req.settings.systemPrompt : undefined;
}

/** `data:` / `http(s):` 图片 URL -> Gemini part。 */
function imagePart(url: string): GeminiPart | undefined {
  if (/^https?:\/\//i.test(url)) return { fileData: { fileUri: url } };

  const comma = url.indexOf(',');
  if (url.startsWith('data:') && comma > 0 && url.slice(0, comma).includes(';base64')) {
    const mediaType = url.slice(5, comma).split(';')[0] ?? 'application/octet-stream';
    return { inlineData: { mimeType: mediaType, data: url.slice(comma + 1) } };
  }
  return undefined;
}

/** 内部 ChatMessage -> Gemini `contents`（system 角色不进入 contents）。 */
export function toGeminiContents(req: ChatRequest): GeminiContent[] {
  const contents: GeminiContent[] = [];

  for (const message of req.messages) {
    if (message.role === 'system') continue;

    const role: GeminiContent['role'] = message.role === 'assistant' ? 'model' : 'user';
    const images = message.role === 'user' ? (message.images ?? []) : [];

    const parts: GeminiPart[] = [];
    if (message.content) parts.push({ text: message.content });
    for (const url of images) {
      const part = imagePart(url);
      if (part) parts.push(part);
    }

    if (parts.length === 0) parts.push({ text: '' });
    contents.push({ role, parts });
  }

  return contents;
}

/** 内部 ToolSpec -> Gemini `tools[].functionDeclarations`。 */
export function toGeminiTools(tools?: ToolSpec[]): Record<string, unknown>[] | undefined {
  if (!tools || tools.length === 0) return undefined;
  return [
    {
      functionDeclarations: tools.map((tool) => ({
        name: tool.name,
        description: tool.description,
        parameters: tool.inputSchema,
      })),
    },
  ];
}

/** 组装请求体；参数按 ModelSettings 映射。 */
export function buildGeminiBody(req: ChatRequest): GeminiRequestBody {
  const body: GeminiRequestBody = {
    contents: toGeminiContents(req),
    generationConfig: {
      temperature: req.settings.temperature,
      topP: req.settings.topP,
      maxOutputTokens: normalizeMaxTokens(req.settings.maxOutputTokens),
    },
  };

  const system = collectSystemPrompt(req);
  if (system) body.systemInstruction = { parts: [{ text: system }] };

  const budget = THINKING_BUDGET[req.settings.thinkingLevel];
  if (budget !== undefined) {
    body.generationConfig = { ...body.generationConfig, thinkingConfig: { thinkingBudget: budget } };
  }

  const tools = toGeminiTools(req.tools);
  if (tools) body.tools = tools;

  return body;
}

interface GeminiUsageShape {
  promptTokenCount?: number;
  candidatesTokenCount?: number;
}

interface GeminiResponseShape {
  candidates?: { content?: { parts?: GeminiPart[] } }[];
  usageMetadata?: GeminiUsageShape;
  error?: { message?: string; status?: string };
  promptFeedback?: { blockReason?: string };
}

export interface ParsedGeminiPayload {
  chunks: StreamChunk[];
  usage?: StreamUsage;
}

function toUsage(usage?: GeminiUsageShape): StreamUsage | undefined {
  if (!usage) return undefined;
  return {
    promptTokens: usage.promptTokenCount ?? 0,
    completionTokens: usage.candidatesTokenCount ?? 0,
  };
}

/** 把一个 Gemini 响应对象里的 parts 拆成 reasoning / text 分片。 */
function partsToChunks(response: GeminiResponseShape | null): StreamChunk[] {
  const chunks: StreamChunk[] = [];
  let toolIndex = 0;
  for (const candidate of response?.candidates ?? []) {
    for (const part of candidate.content?.parts ?? []) {
      const call = part.functionCall ?? part.function_call;
      if (call && typeof call.name === 'string' && call.name !== '') {
        chunks.push({
          type: 'tool_call',
          delta: JSON.stringify({
            index: toolIndex++,
            name: call.name,
            arguments: JSON.stringify(call.args ?? {}),
          }),
        });
      }
      if (typeof part.text === 'string' && part.text.length > 0) {
        chunks.push(part.thought === true ? { type: 'reasoning', delta: part.text } : { type: 'text', delta: part.text });
      }
    }
  }
  return chunks;
}

/**
 * 解析一条 SSE `data:` 负载为若干 StreamChunk。
 *
 * `alt=sse` 模式下 Gemini 每条 `data:` 都是一个完整的响应对象，
 * 与 OpenAI 的 `[DONE]` 不同，这里不做哨兵处理。
 */
export function mapGeminiStreamPayload(payload: string): ParsedGeminiPayload {
  const trimmed = payload.trim();
  if (!trimmed || trimmed === '[DONE]') return { chunks: [] };

  let parsed: GeminiResponseShape;
  try {
    parsed = JSON.parse(trimmed) as GeminiResponseShape;
  } catch {
    // 单个坏分片不应中断整个回答，跳过即可。
    return { chunks: [] };
  }

  if (parsed.error) {
    return {
      chunks: [{ type: 'error', error: parsed.error.message ?? 'Provider 返回错误', code: 'http' }],
    };
  }

  const chunks = partsToChunks(parsed);
  const usage = toUsage(parsed.usageMetadata);
  return usage ? { chunks, usage } : { chunks };
}

/** 非流式响应 -> StreamChunk[]（思考 + 一次文本 + 恰好一个 done）。 */
export function mapGeminiResponse(json: unknown): StreamChunk[] {
  const parsed = json as GeminiResponseShape | null;
  if (parsed?.error) {
    return [{ type: 'error', error: parsed.error.message ?? 'Provider 返回错误', code: 'http' }];
  }

  const chunks = partsToChunks(parsed);
  if (chunks.every((chunk) => chunk.type !== 'text')) {
    chunks.push({ type: 'text', delta: '' });
  }

  const usage = toUsage(parsed?.usageMetadata);
  chunks.push(usage ? { type: 'done', usage } : { type: 'done' });
  return chunks;
}

function errorChunk(error: unknown): StreamChunk {
  const normalized = toProviderError(error);
  return {
    type: 'error',
    error: normalized.message,
    code: normalized.code,
    status: normalized.status,
  };
}

export class GeminiAdapter implements ProviderAdapter {
  readonly id: string;
  readonly kind = GEMINI_KIND;
  private readonly config: ResolvedProviderConfig;
  private readonly fetchImpl: FetchLike;
  private readonly timeoutMs: number;
  private readonly retries: number;
  private readonly retryDelayMs: number;
  private readonly extraHeaders: Record<string, string>;

  constructor(config: ResolvedProviderConfig, deps?: ProviderDeps) {
    const resolved = resolveDeps(deps);
    this.config = config;
    this.id = config.id;
    this.fetchImpl = resolved.fetch;
    // 配置值优先，但一律过边界校验（负数 / NaN 不得绕过钳制）。
    this.timeoutMs = clampTimeoutMs(config.timeoutMs, resolved.timeoutMs);
    this.retries = clampRetries(config.retries, resolved.retries);
    this.retryDelayMs = clampRetryDelayMs(config.retryDelayMs, resolved.retryDelayMs);
    // 自定义头：ProviderConfig.headers 覆盖 ProviderDeps.extraHeaders。
    this.extraHeaders = { ...resolved.extraHeaders, ...(config.headers ?? {}) };
  }

  private headers(stream: boolean): Record<string, string> {
    return geminiHeaders({ stream, apiKey: this.config.apiKey, extra: this.extraHeaders });
  }

  private deps() {
    return {
      fetch: this.fetchImpl,
      timeoutMs: this.timeoutMs,
      retries: this.retries,
      retryDelayMs: this.retryDelayMs,
      extraHeaders: this.extraHeaders,
    };
  }

  async listModels(): Promise<string[]> {
    return readModelCatalog<{ models?: { name?: string }[]; nextPageToken?: string }>(geminiModelsUrl(this.config.baseUrl, this.config.apiKey), this.headers(false), this.deps(), json => {
      const models = (json.models ?? [])
        .map((item) => item.name)
        .filter((name): name is string => typeof name === 'string' && name.length > 0)
        // `models/gemini-2.5-pro` -> `gemini-2.5-pro`
        .map((name) => name.replace(/^models\//, ''));
      return { models, ...(json.nextPageToken ? { next: ['pageToken', json.nextPageToken] as [string, string] } : {}) };
    });
  }

  async *chat(req: ChatRequest): AsyncIterable<StreamChunk> {
    // 契约：取消 = 静默结束，不产出任何分片（含 error）。
    if (req.signal?.aborted) return;

    let opened: OpenResponseResult;
    try {
      opened = await openResponseWithRetry({
        url: req.settings.stream
          ? geminiStreamUrl(this.config.baseUrl, req.model, this.config.apiKey)
          : geminiGenerateUrl(this.config.baseUrl, req.model, this.config.apiKey),
        init: {
          method: 'POST',
          headers: this.headers(req.settings.stream),
          body: JSON.stringify(buildGeminiBody(req)),
        },
        deps: this.deps(),
        externalSignal: req.signal,
      });
    } catch (error) {
      if (req.signal?.aborted) return;
      yield errorChunk(error);
      return;
    }

    const { response, guard } = opened;
    // 是否「提前」结束读取：提前结束时必须主动中止底层 fetch，释放悬挂连接。
    let earlyExit = false;

    try {
      if (!req.settings.stream) {
        const json = await readJsonBody(response);
        for (const chunk of mapGeminiResponse(json)) yield chunk;
        return;
      }

      if (!response.body) {
        earlyExit = true;
        yield { type: 'error', error: '响应没有可读流', code: 'parse' };
        return;
      }

      let usage: StreamUsage | undefined;
      for await (const payload of readSSEData(response.body, guard.signal)) {
        if (req.signal?.aborted) {
          earlyExit = true;
          return;
        }
        guard.reset();

        const parsed = mapGeminiStreamPayload(payload);
        if (parsed.usage) usage = parsed.usage;
        for (const chunk of parsed.chunks) {
          if (chunk.type === 'error') {
            earlyExit = true;
            yield chunk;
            return;
          }
          yield chunk;
        }
      }

      if (req.signal?.aborted) {
        earlyExit = true;
        return;
      }
      if (guard.timedOut()) {
        earlyExit = true;
        yield { type: 'error', error: `请求超时（${this.timeoutMs}ms）`, code: 'timeout' };
        return;
      }

      // 无论服务端如何收尾，都在结束时补一次 done，保证上层收尾一致。
      yield usage ? { type: 'done', usage } : { type: 'done' };
    } catch (error) {
      earlyExit = true;
      if (req.signal?.aborted) return;
      if (guard.timedOut()) {
        yield { type: 'error', error: `请求超时（${this.timeoutMs}ms）`, code: 'timeout' };
        return;
      }
      if (isAbortError(error)) return;
      yield errorChunk(error);
    } finally {
      if (earlyExit) guard.abort();
      guard.dispose();
    }
  }
}

export function createGeminiAdapter(
  config: ResolvedProviderConfig,
  deps?: ProviderDeps,
): ProviderAdapter {
  return new GeminiAdapter(config, deps);
}
